import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../components/Toast';
import { ApiService } from '../utils/api';

const MyRegistrationLookupPage = () => {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event');
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState([]);

  const { data: event } = useQuery({
    queryKey: ['publicEvent', eventId],
    queryFn: () => ApiService.getPublicEvent(eventId),
    enabled: !!eventId,
    retry: false,
  });

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await ApiService.requestMyRegistrationOtp(eventId, email.trim());
      showSuccess(result.message || 'Check your inbox for the code.');
      setStep('code');
    } catch (err) {
      showError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const found = await ApiService.verifyMyRegistrationOtp(eventId, email.trim(), code.trim());
      if (found.length === 1) {
        navigate(`/my-registration/${found[0].token}`, { replace: true });
        return;
      }
      setMatches(found);
      setStep('pick');
    } catch (err) {
      showError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-xl">My registration</CardTitle>
          {event?.name && <p className="text-sm text-gray-500">{event.name}</p>}
        </CardHeader>
        <CardContent>
          {!eventId && (
            <p className="text-sm text-gray-600">Open this page from the event's registration page, or use the link in your confirmation email.</p>
          )}
          {eventId && step === 'email' && (
            <form onSubmit={sendCode} className="space-y-3">
              <div>
                <Label htmlFor="email">Email you registered with</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <Button type="submit" className="w-full" disabled={busy || !email.trim()}>Email me a code</Button>
            </form>
          )}
          {eventId && step === 'code' && (
            <form onSubmit={verify} className="space-y-3">
              <div>
                <Label htmlFor="code">6-digit code sent to {email}</Label>
                <Input id="code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
              </div>
              <Button type="submit" className="w-full" disabled={busy || code.length !== 6}>Open my registration</Button>
              <Button type="button" variant="ghost" className="w-full" disabled={busy} onClick={() => { setCode(''); setStep('email'); }}>
                Use a different email
              </Button>
            </form>
          )}
          {step === 'pick' && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">This email has more than one registration. Pick one:</p>
              {matches.map((match) => (
                <Button key={match.token} variant="outline" className="w-full justify-between"
                  onClick={() => navigate(`/my-registration/${match.token}`)}>
                  <span className="truncate capitalize">{match.name || match.userId}</span>
                  <span className="text-xs text-gray-500 font-mono">{match.userId}</span>
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyRegistrationLookupPage;
